import { createTrackingConfig, getLabels } from '../../components/generic/labels';
import { getDateFormat, tableConfig, uiConfig } from '../../config/appConfig';

const productStatusChangeTrackingLabels = getLabels('Product Status Change');
const productStatusChangeTrackingBase =
  createTrackingConfig('Product Status Change');

export const productStatusChangeConfig = {
  entityName: 'ProductStatusChange',
  
  // Entity information for file attachments and audit logs
  entityTypeId: 16, // Product Status Change entity type ID
  
  // Backend API Configuration
  api: {
    baseEndpoint: '/product-status-change',
    endpoints: {
      list: '',
      create: '',
      update: (id) => `/${id}`,
      delete: (id) => `/${id}`,
      getById: (id) => `/${id}`,
      stats: '/stats',
      findByStatus: (statusId) => `/status/${statusId}`,
    },
    lookupEndpoints: {
      productStatusChangeStatuses: '/product-status-change-status',
      users: '/users',
    },
    swrInvalidation: {
      mainList: '/product-status-change',
      withFileAttachments: '/product-status-change/with-file-attachments',
      fileAttachments: (entityTypeId) => `/file-attachment/entity/${entityTypeId}/*`
    }
  },
  
  // UI Configuration - Using centralized app config
  ui: {
    drawerWidth: '50%',
    defaultPageSize: tableConfig.defaultPageSize,
    pageSizeOptions: tableConfig.pageSizeOptions,
    defaultTableDensity: tableConfig.defaultDensity,
    requiredFieldStyle: uiConfig.requiredFieldStyle,
    tableVariant: 'default',  // 'default' | 'modern' | 'cards' | 'board' | 'timeline' | 'advancedCard'
                            // default: default table with cards
                            // cards: cards table
                            // board: board table
                            // timeline: timeline table
                            // advancedCard: advanced card table
    splitView: true,
    searchInputMaxWidth: uiConfig.searchInputMaxWidth,
    rowPositioning: 'auto',
    customRowOrder: [],
    columnPositioning: 'auto',
    showViewButton: true,
    showEditButton: true,
    showDeleteButton: true,
    showDuplicateButton: false,
  },

  // Custom Split View Panel configuration
  customSplitViewPanel: {
    enabled: true,
    component: null // Will be imported in productStatusChangePage
  },

  // Features configuration
  features: {
    fileAttachments: true, // Enable file attachment functionality
    masterEntityTypeId: 16, // Product Status Change master entity type ID
    approvalRequests: true, // Enable approval request functionality
  },

  // Review and Approval configuration
  approval: {
    enabled: true, // Master switch for review and approval functionality
    showInForm: true, // Show review and approval request button in product status change form
    showInDetails: true, // Show review and approval integration in product status change details tab
    requireApprovalForStatusChange: false, // Require review and approval before status change
    allowMultipleApprovals: true, // Allow multiple review and approval requests per product status change
    defaultApprovalType: 'single', // Default approval type when creating requests
    approvalButtonText: 'Request Review and Approval', // Text for review and approval request button
    approvalButtonIcon: 'CheckCircleIcon', // Icon component name (from Icons.js) for approval button icon
    showApprovalStatus: true, // Show current review and approval status in product status change view
    showApprovalActions: true, // Show approval action buttons (approve, reject, etc.)
    showApprovalHistory: true, // Show review and approval history in product status change timeline
    formMode: 'modal', // Use modal popup for review and approval form
  },

  // Custom handlers
  customHandlers: {
    onSave: null,
    onDelete: null,
    onLoad: null,
    onFormOpen: null,
  },
  
  // Service configuration
  service: {
    name: 'productStatusChangeService',
    methods: {
      getAll: 'getAllProductStatusChanges',
      getById: 'getProductStatusChangeById', 
      create: 'createProductStatusChange',
      update: 'updateProductStatusChange',
      delete: 'deleteProductStatusChange',
      validate: 'validateData'
    }
  },
  
  // Display Configuration
  displayName: 'Product Status Change Management',
  description: 'Manage product status changes and QP confirmations',
  icon: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z',
  
  // Fields to remove from forms (relations, computed, system fields)
  fieldsToRemove: [
    'id', 'productStatus', 'qpUser', 'createdBy', 'updatedBy', 'createdById', 'updatedById',
    'createdAt', 'updatedAt', 'fileAttachments', 'fileAttachmentCount'
  ],

  // UNIFIED FIELD CONFIGURATION
  fields: [
    { 
      name: 'id', 
      label: 'ID',
      type: 'hidden',
      table: false,
      filterable: false
    },
    // Product Information Section
    {
      name: 'productName',
      label: 'Product Name',
      type: 'text',
      required: true,
      placeholder: 'Enter product name',
      table: { 
        header: 'Product Name', 
        width: '200px', 
        sortable: true 
      },
      filterable: true,
      validation: {
        required: true,
        maxLength: 200,
        message: 'Product name is required and must not exceed 200 characters'
      }
    },
    {
      name: 'itemCode',
      label: 'Item Code',
      type: 'text',
      required: false,
      placeholder: 'Enter item code',
      table: { 
        header: 'Item Code', 
        width: '120px', 
        sortable: true 
      },
      filterable: true,
      validation: {
        maxLength: 100,
        message: 'Item code must not exceed 100 characters'
      }
    },
    {
      name: 'batchNumber',
      label: 'Batch Number',
      type: 'text',
      required: true,
      placeholder: 'Enter batch number',
      table: { 
        header: 'Batch Number', 
        width: '150px', 
        sortable: true 
      },
      filterable: true,
      validation: {
        required: true,
        maxLength: 100,
        message: 'Batch number is required and must not exceed 100 characters'
      }
    },
    {
      name: 'expiryDate',
      label: 'Expiry Date',
      type: 'date',
      required: false,
      table: { 
        header: 'Expiry Date', 
        width: tableConfig.dateColumnWidth,
        sortable: true,
        cellRenderer: 'date',
        dateFormat: getDateFormat('table')
      },
      filterable: true,
      filterType: 'dateRange',
      validation: {
        message: 'Please enter a valid expiry date'
      }
    },
    {
      name: 'quantity',
      label: 'Quantity',
      type: 'number',
      required: false,
      placeholder: 'Enter quantity',
      table: { 
        header: 'Quantity', 
        width: '100px', 
        sortable: true 
      },
      filterable: true,
      validation: {
        integer: true,
        min: 0,
        message: 'Quantity must be a positive integer'
      }
    },
    // Merck References Section
    {
      name: 'merckReference',
      label: 'Merck Reference (ManGo ID)',
      type: 'text',
      required: false,
      placeholder: 'Enter Merck reference',
      table: { 
        header: 'Merck Reference', 
        width: '150px', 
        sortable: true 
      },
      filterable: true,
      validation: {
        maxLength: 100,
        message: 'Merck reference must not exceed 100 characters'
      }
    },
    {
      name: 'merckDispositionCertDate',
      label: 'Merck Disposition Cert Date',
      type: 'date',
      required: false,
      table: { 
        header: 'Merck Cert Date', 
        width: tableConfig.dateColumnWidth,
        sortable: true,
        cellRenderer: 'date',
        dateFormat: getDateFormat('table')
      },
      filterable: true,
      filterType: 'dateRange',
      validation: {
        message: 'Please enter a valid date'
      }
    },
    // Status Section
    {
      name: 'productStatusId',
      label: 'Product Status',
      type: 'select',
      required: true,
      lookupEndpoint: 'productStatusChangeStatuses',
      options: [],
      validation: {
        required: true,
        integer: true,
        message: 'Please select a valid product status'
      },
      table: {
        header: 'Status',
        width: '150px',
        sortable: true,
        displayField: 'productStatus',
        cellRenderer: 'badge'
      },
      filterable: true,
      filterType: 'multiSelect'
    },
    // Comments Section
    {
      name: 'remarks',
      label: 'Document Review and Approval Status',
      type: 'textarea',
      placeholder: 'Enter document review and approval status comments',
      required: false,
      validation: {
        maxLength: 2000,
        message: 'Remarks must not exceed 2000 characters'
      },
      table: { 
        header: 'Document Review and Approval Status', 
        width: '300px', 
        sortable: false,
        cellRenderer: 'lineClamp',
        maxLines: 2
      },
      filterable: false,
      rows: 4
    },
    // QP Confirmation Section
    {
      name: 'qpUserId',
      label: 'QP User',
      type: 'select',
      required: true,
      lookupEndpoint: 'users',
      lookupFilter: (user) => user?.isQMS_PSC === true,
      options: [],
      validation: {
        required: true,
        integer: true,
        message: 'QP User is required'
      },
      table: {
        header: 'QP User',
        width: '150px',
        sortable: true,
        displayField: 'qpUser',
        cellRenderer: 'user'
      },
      filterable: true,
      filterType: 'multiSelect'
    },
    {
      name: 'qpUserSignedAt',
      label: 'QP User Signed At',
      type: 'datetime-local',
      required: true,
      table: { 
        header: 'QP Signed', 
        width: tableConfig.dateTimeColumnWidth,
        sortable: true,
        cellRenderer: 'dateTime',
        dateFormat: getDateFormat('datetime')
      },
      filterable: true,
      filterType: 'dateRange',
      validation: {
        required: true,
        message: 'QP User Signed At is required'
      }
    },
    // File Attachments
    { 
      name: 'fileAttachments', 
      label: 'File(s)', 
      type: 'fileUpload',
      useFormFileUpload2: true, // Use the new responsive FormFileUpload_2 component
      multiple: true,
      accept: '.pdf,.doc,.docx,.txt,.md,image/*',
      maxSize: 50 * 1024 * 1024, // 50MB
      maxFiles: 10,
      placeholder: 'Drop files here or click to browse',
      table: { 
        header: 'File(s)', 
        width: '50px',
        sortable: false,
        displayField: 'fileAttachmentCount',
        cellRenderer: 'fileCount'
      },
      filterable: false,
      validation: {
        required: false
      }
    },
  ],

  // Form Sections Configuration
  formSections: [
    {
      name: 'productInformation',
      title: 'Product Information',
      icon: '📦',
      layout: 'grid',
      gridCols: 'md:grid-cols-4',
      fieldSpans: {
        'productName': 'col-span-full md:col-span-3',
        'itemCode': 'col-span-full md:col-span-1',
        'batchNumber': 'col-span-full md:col-span-1',
        'expiryDate': 'col-span-full md:col-span-1',
        'quantity': 'col-span-full md:col-span-1'
      },
      fields: [
        'productName',
        'itemCode',
        'batchNumber',
        'expiryDate',
        'quantity'
      ]
    },
    {
      name: 'merckReferences',
      title: 'Merck References',
      icon: '🏢',
      layout: 'grid',
      gridCols: 'md:grid-cols-2',
      fieldSpans: {
        'merckReference': 'col-span-full md:col-span-1',
        'merckDispositionCertDate': 'col-span-full md:col-span-1'
      },
      fields: [
        'merckReference',
        'merckDispositionCertDate'
      ]
    },
    {
      name: 'statusAndComments',
      title: 'Document Review and Approval Status',
      icon: '📊',
      fields: [
        'productStatusId',
        'remarks'
      ]
    },
    {
      name: 'qpConfirmation',
      title: 'QP Confirmation',
      icon: '✍️',
      layout: 'grid',
      gridCols: 'md:grid-cols-2',
      fieldSpans: {
        'qpUserId': 'col-span-full md:col-span-1',
        'qpUserSignedAt': 'col-span-full md:col-span-1'
      },
      fields: [
        'qpUserId',
        'qpUserSignedAt'
      ]
    },
    {
      name: 'fileAttachments',
      title: 'File Attachments',
      icon: '📎',
      layout: 'full',
      fields: ['fileAttachments']
    }
  ],

  // Transform data before sending to API
  transformData: (data, formMode) => {
    const transformed = { ...data };
    
    // Remove system and relation fields that shouldn't be sent to API
    const fieldsToRemove = [
      'id', 'productStatus', 'qpUser', 'createdBy', 'updatedBy', 
      'createdById', 'updatedById', 'createdAt', 'updatedAt',
      'fileAttachments', 'fileAttachmentCount'
    ];
    fieldsToRemove.forEach(field => {
      delete transformed[field];
    });
    
    // Convert ID fields to numbers
    const idFields = ['productStatusId', 'qpUserId'];
    idFields.forEach(field => {
      if (transformed[field] !== undefined) {
        if (transformed[field] === '' || transformed[field] === null) {
          delete transformed[field];
        } else {
          const numValue = Number(transformed[field]);
          if (!isNaN(numValue)) {
            transformed[field] = numValue;
          } else {
            delete transformed[field];
          }
        }
      }
    });

    // Convert date fields to ISO strings or remove if empty
    const dateFields = ['expiryDate', 'merckDispositionCertDate', 'qpUserSignedAt'];
    dateFields.forEach(field => {
      if (transformed[field] !== undefined) {
        if (transformed[field] === '' || transformed[field] === null) {
          delete transformed[field];
        } else {
          // Convert to ISO string if it's a valid date
          const dateValue = new Date(transformed[field]);
          if (!isNaN(dateValue.getTime())) {
            transformed[field] = dateValue.toISOString();
          } else {
            delete transformed[field];
          }
        }
      }
    });

    // Convert quantity to number
    if (transformed.quantity !== undefined) {
      if (transformed.quantity === '' || transformed.quantity === null) {
        delete transformed.quantity;
      } else {
        const numValue = Number(transformed.quantity);
        if (!isNaN(numValue) && numValue >= 0) {
          transformed.quantity = numValue;
        } else {
          delete transformed.quantity;
        }
      }
    }
    
    return transformed;
  },

  // Validation configuration
  validationConfig: {
    enableRealTimeValidation: true,
    validateOnBlur: true,
    validateOnChange: false,
    showValidationSummary: true
  },

  // Notification messages
  messages: {
    loading: 'Loading product status changes...',
    noData: 'No product status changes found. Create your first record to get started.',
    createSuccess: 'Product status change created successfully!',
    updateSuccess: 'Product status change updated successfully!',
    deleteSuccess: 'Product status change deleted successfully!',
    createError: 'Failed to create product status change',
    updateError: 'Failed to update product status change', 
    deleteError: 'Failed to delete product status change',
    loadError: 'Failed to load product status changes',
    validationError: 'Validation failed'
  },

  // COMPUTED CONFIGURATIONS
  get tableColumns() {
    if (!this.fields || !Array.isArray(this.fields)) {
      console.warn('⚠️ productStatusChangeConfig.fields is not available or not an array');
      return [];
    }
    
    const columns = this.fields
      .filter(field => field.table !== false && field.table)
      .map(field => ({
        key: field.table.displayField || field.name,
        header: field.table.header || field.label,
        width: field.table.width || 'auto',
        sortable: field.table.sortable !== false,
        displayField: field.table.displayField || field.name,
        cellRenderer: field.table.cellRenderer || 'default',
        dateFormat: field.table.dateFormat,
        originalField: field.name,
        maxLines: field.table.maxLines,
        maxLength: field.table.maxLength
      }));
    
    return columns;
  },

  // Filter Groups Configuration
  filterGroups: [
    {
      name: 'product',
      label: 'Product Information',
      icon: '📦',
      fields: [
        'productName', 'itemCode', 'batchNumber', 'expiryDate', 'quantity'
      ]
    },
    {
      name: 'merck',
      label: 'Merck References',
      icon: '🏢',
      fields: [
        'merckReference', 'merckDispositionCertDate'
      ]
    },
    {
      name: 'status',
      label: 'Status',
      icon: '📊',
      fields: [
        'productStatusId'
      ]
    },
    {
      name: 'qp',
      label: 'QP Confirmation',
      icon: '✍️',
      fields: [
        'qpUserId', 'qpUserSignedAt'
      ]
    }
  ],

  exportFileName: 'qms_product_status_changes',

  // Backend field mappings
  fieldMappings: {
    productStatus: 'productStatus.name',
    qpUser: 'qpUser.userDisplayName',
    createdBy: 'createdBy.userDisplayName',
    updatedBy: 'updatedBy.userDisplayName'
  },
  
  get filterableFields() {
    return this.fields
      .filter(field => field.filterable === true)
      .map(field => field.name);
  },

  get validationRules() {
    const rules = {};
    this.fields.forEach(field => {
      if (field.validation) {
        rules[field.name] = field.validation;
      }
    });
    return rules;
  },

  // Tracking Section — show audit users by display name (user objects from API / selected row)
  trackingSection: {
    ...productStatusChangeTrackingBase,
    fields: productStatusChangeTrackingBase.fields.map((f) =>
      f.id === 'created'
        ? {
            ...f,
            userLabel: 'Created user:',
            dateLabel: productStatusChangeTrackingLabels.createdDateLabel,
            userEmptyText: productStatusChangeTrackingLabels.notSpecifiedText,
            dateEmptyText: productStatusChangeTrackingLabels.notSpecifiedText,
          }
        : f.id === 'updated'
          ? {
              ...f,
              userLabel: 'Updated user:',
              dateLabel: productStatusChangeTrackingLabels.updatedDateLabel,
              userEmptyText: productStatusChangeTrackingLabels.notModifiedText,
              dateEmptyText: productStatusChangeTrackingLabels.neverUpdatedText,
            }
          : f,
    ),
  },

  // Labels and Text Configuration
  labels: {
    singular: 'Product Status Change',
    plural: 'Product Status Changes',
    pageTitle: 'Product Status Change Management',
    pageSubtitle: 'Manage product status changes and QP confirmations',
    searchPlaceholder: 'Search by product name, batch number, or item code...',
    viewTitle: 'View Product Status Change',
    noSelectionText: 'Select a product status change to see details'
  },

  // Validation function for real-time field validation
  validateField: null,

  // Approval helper functions
  isApprovalEnabled: function() {
    return this.features.approvalRequests && this.approval.enabled;
  },

  shouldShowApprovalInForm: function() {
    return this.isApprovalEnabled() && this.approval.showInForm;
  },

  shouldShowApprovalInDetails: function() {
    return this.isApprovalEnabled() && this.approval.showInDetails;
  },

  // Help text for field descriptions
  helpText: {
    productName: { short: 'Name of the product for which the status is being changed.' },
    itemCode: { short: 'Item code or SKU of the product.' },
    batchNumber: { short: 'Batch or lot number of the product.' },
    expiryDate: { short: 'Expiry date of the product batch.' },
    quantity: { short: 'Quantity of the product affected by this status change.' },
    merckReference: { short: 'Merck reference number or ManGo ID.' },
    merckDispositionCertDate: { short: 'Date of Merck disposition certificate.' },
    productStatusId: { short: 'Current status of the product.' },
    remarks: { short: 'Additional comments or notes about this status change.' },
    qpUserId: { short: 'Qualified Person (QP) who confirmed this status change.' },
    qpUserSignedAt: { short: 'Date and time when the QP signed/confirmed this status change.' },
    fileAttachments: { short: 'Supporting documents, evidence, or related files for this product status change.' }
  }
};

export default productStatusChangeConfig;

